import { Dispatch } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from 'redux';

import lang from '../../lib/language';
import { BaseStatementSearchParamType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getStatementsCashFlow } from '../../lib/store/fundamental';

import { FinancialsTableRow } from '../../components';
import FinancialsDivRow from '../../shared_components/FinancialsDivRow/FinancialsDivRow';

import FinancialStatementLayout from './FinancialStatementLayout';
import useFinancialStatement from './useFinancialStatement';

const PINNED_COLUMN = 1;

const CashFlowStatement = (props: ReduxProps) => {
  const { getStatements, cashFlow } = props;

  const {
    headers,
    isMobile,
    loading,
    period,
    row,
    selectedColumn,
    selectedPeriodIndex,
    selectPeriod,
    setSelectedPeriodIndex,
    timeOptions,
  } = useFinancialStatement({
    items: cashFlow,
    isReady: cashFlow.length > 0,
    labelPrefix: 'cashFlow',
    getStatements,
  });

  return (
    <FinancialStatementLayout
      title={lang.financialsCashFlow()}
      preloaderOwner="cashflow-statement"
      loading={loading}
      isMobile={isMobile}
      period={period}
      onPeriodChange={selectPeriod}
      headers={headers}
      timeOptions={timeOptions}
      selectedPeriodIndex={selectedPeriodIndex}
      onPeriodIndexChange={setSelectedPeriodIndex}
      mobileRows={(
        <>
          <FinancialsTableRow data={[ row.operatingCashFlow ]} isClickable currentPeriod={selectedColumn}>
            <FinancialsTableRow data={[ row.netIncome ]} currentPeriod={selectedColumn} isBaseFontRow />
            <FinancialsTableRow
              data={[ row.depreciationAndAmortization ]}
              currentPeriod={selectedColumn}
              isBaseFontRow
            />
            <FinancialsTableRow
              data={[ row.deferredIncomeTax ]}
              currentPeriod={selectedColumn}
              isBaseFontRow
            />
            <FinancialsTableRow
              data={[ row.stockBasedCompensation ]}
              currentPeriod={selectedColumn}
              isBaseFontRow
            />
            <FinancialsTableRow
              data={[
                row.changeInWorkingCapital,
                row.accountsReceivables,
                row.inventory,
                row.accountsPayables,
                row.otherWorkingCapital,
              ]}
              isClickable
              currentPeriod={PINNED_COLUMN}
              isMaxNestLevel
            />
            <FinancialsTableRow
              data={[ row.otherNonCashItems ]}
              currentPeriod={selectedColumn}
              isBaseFontRow
            />
          </FinancialsTableRow>
          <FinancialsTableRow
            data={[
              row.netCashUsedForInvestingActivities,
              row.investmentsInPropertyPlantAndEquipment,
              row.acquisitionsNet,
              row.salesMaturitiesOfInvestments,
              row.purchasesOfInvestments,
              row.otherInvestingActivities,
            ]}
            isClickable
            currentPeriod={PINNED_COLUMN}
          />
          <FinancialsTableRow
            data={[
              row.netCashUsedProvidedByFinancingActivities,
              row.debtRepayment,
              row.commonStockRepurchased,
              row.dividendsPaid,
              row.otherFinancingActivities,
            ]}
            isClickable
            currentPeriod={PINNED_COLUMN}
          />
          <FinancialsTableRow
            data={[ row.cashAtEndOfPeriod, row.netChangeInCash, row.cashAtBeginningOfPeriod ]}
            isClickable
            currentPeriod={PINNED_COLUMN}
          />
          <FinancialsTableRow data={[ row.capitalExpenditure ]} currentPeriod={PINNED_COLUMN} />
          <FinancialsTableRow data={[ row.freeCashFlow ]} currentPeriod={PINNED_COLUMN} />
        </>
      )}
      desktopRows={(
        <>
          <FinancialsDivRow
            data={row.operatingCashFlow}
            subData={[
              row.netIncome,
              row.depreciationAndAmortization,
              row.deferredIncomeTax,
              row.stockBasedCompensation,
              row.changeInWorkingCapital,
              row.otherNonCashItems,
            ]}
            isClickable
            extraSubData={[ [], [], [], [], [
              row.accountsReceivables,
              row.inventory,
              row.accountsPayables,
              row.otherWorkingCapital,
            ] ]}
          />
          <FinancialsDivRow
            data={row.netCashUsedForInvestingActivities}
            subData={[
              row.investmentsInPropertyPlantAndEquipment,
              row.acquisitionsNet,
              row.salesMaturitiesOfInvestments,
              row.purchasesOfInvestments,
              row.otherInvestingActivities,
            ]}
            isClickable
          />
          <FinancialsDivRow
            data={row.netCashUsedProvidedByFinancingActivities}
            subData={[
              row.debtRepayment,
              row.commonStockRepurchased,
              row.dividendsPaid,
              row.otherFinancingActivities,
            ]}
            isClickable
          />
          <FinancialsDivRow
            data={row.cashAtEndOfPeriod}
            subData={[ row.netChangeInCash, row.cashAtBeginningOfPeriod ]}
            isClickable
          />
          <FinancialsDivRow data={row.capitalExpenditure} />
          <FinancialsDivRow data={row.freeCashFlow} />
        </>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  cashFlow: state.fundamental.statementsData.cashFlow,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsCashFlow(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CashFlowStatement);
